/**
 * index.ts
 * [Readme] --------------------------------------------------------------------------------------
 *
 * 데스크탑 브랜드&분양페이지 Header 렌더링 컴포넌트
 *
 * -----------------------------------------------------------------------------------------------
 */

 import { HideText } from 'common-modules/general/assets/Styles/Common.style';
 import useSticky from 'common-modules/general/Modules/custom-hooks/useSticky';
 import { RoutesString } from 'common-modules/general/Config/routesString';
 import MobxStore from 'common-modules/general/Store/MobxStore';
 import { observer } from 'mobx-react';
 import { useEffect, useState } from 'react';
 import { useLocation } from 'react-router';
 import styled, { css } from 'styled-components';
 
 import { GnbWrapper } from './gnbWrapper';
 import UtilWrapper from './utilWrapper';
 import logo from 'common-modules/general/assets/Desktop/images/common/logo.png';
 
 const HeaderRootContainer = styled.div<{ isMain: boolean }>`
   width: 100%;
   position: absolute;
   z-index: 180;
   min-width: 1300px;
 
   ${props =>
     props.isMain &&
     css`
       &.fixed-root {
         .gnb-wrap {
           border-bottom: 1px solid #eee;
           &:hover {
             border-bottom: 0;
           }
         }
       }
     `};
 `;
 
 const HoverStyle = css`
   &:hover {
     background-color: #fff;
     .gnbWrap {
       border: 1px solid #fff;
     }
 
     .gnb-menu-item span {
       color: #666;
     }
     .header-sub-block {
       display: block;
     }
     .header-util span {
       color: #666;
     }
     .dimm-image {
       display: none;
     }
   }
 `;
 
 const HeaderWrapper = styled.header`
   .header-util span {
     color: #666;
   }
 
   &.disabled-active {
     .header-util span {
       color: #fff;
     }
     background: transparent;
 
     ${HoverStyle};
   }
 
   &.sticky {
     background-color: #fff;
     .header-util span {
       color: #666;
     }
     .gnb-wrap {
       box-sizing: border-box;
     }
 
     .dimm-image {
       display: none;
     }
 
     ${HoverStyle};
   }
 `;
 
 const HeaderContent = styled.div`
   width: 100%;
 `;
 
 const HeaderLogo = styled.h1`
   a {
     position: absolute;
     z-index: 3;
     width: 78px;
     width: 4.875rem;
     height: 37px;
     height: 2.3125rem;
     top: 23px;
     top: 1.4375rem;
     left: 60px;
     left: 3.75rem;
     background: url(${logo}) no-repeat left top;
     background-size: 78px 37px;
     background-size: 4.875rem 2.3125rem;
   }
 `;
 
 const SkipNav = styled.ul`
   position: absolute;
   top: 0;
   left: 0;
   z-index: 9999;
   width: 100%;
   height: 0;
   line-height: 1;
   a {
     display: block;
     overflow: hidden;
     width: 1px;
     height: 1px;
     margin: 0 -1px -1px 0;
     padding: 0;
     font-size: 0;
     line-height: 1;
   }
 `;
 
 interface IHeader {
   type?: 'main' | 'sub';
 }
 
 const Header = observer(({ type = 'sub' }: IHeader) => {
   const [isMyMenuOpen, setIsMyMenuOpen] = useState<Boolean>(false);
   const [isSearchArea, setIsSearchArea] = useState<Boolean>(false);
   const [headerPos, setHeaderPos] = useState<Number>(0);
 
   const isMain: boolean = type === 'main';
 
   const {
     authManager: { userData }
   } = MobxStore();
 
   const { pathname } = useLocation();
 
   const { isTouched } = useSticky({
     stickyElementStaticContainerId: 'sticky-root-main-nav',
     stickyElementId: 'HeaderWrapper',
     isKeepOriginPosition: false,
     gap: !isMain ? 1 : RoutesString.Main !== pathname ? 0 : 1
   });
 
   const isSrollHeader = () => {
     const el = document.getElementById('fixed-container');
     const header = document.getElementById('gnbWrap');
     const scrollTop = window.scrollY ? window.scrollY : window.pageYOffset;
 
     if (el && header) {
       isSubHeaderPosCheck(el, header, scrollTop);
     }
   };
 
   const isSubHeaderPosCheck = (el: HTMLElement, header: HTMLElement, scrollTop: number) => {
     const overlap = el.offsetTop - header.clientHeight;
     if (scrollTop <= overlap) {
       setHeaderPos(0);
     } else {
       setHeaderPos(overlap - scrollTop >= -95 ? overlap - scrollTop : -95);
     }
   };
 
   useEffect(() => {
    const subHeader = document.getElementsByClassName('header-sub-block')[0] as HTMLElement;

    window.addEventListener('scroll', () => isSrollHeader());
    document.getElementById('HeaderWrapper')?.addEventListener('mouseover', () => {
      const parentClassNameElementList = document.getElementsByClassName('header-sub-block');
      if (parentClassNameElementList.length) {
        if (subHeader.style.display == 'none') {
          subHeader.style.display = 'block';
        }
      }
    });
    subHeader?.addEventListener('mouseleave', () => {
      if (subHeader.style.display == 'block') {
        subHeader.style.display = 'none';
      }
    });
  }, []);

     
   return (
       <>
         <HeaderRootContainer className={isTouched ? 'fixed-root ' : ''} isMain={isMain} id="sticky-root-main-nav">
           <HeaderWrapper
             id="HeaderWrapper"
             style={{ top: `${headerPos}px` }}
             className={`${isTouched ? 'sticky ' : ''}
             ${isMain ? (pathname === RoutesString.Main ? 'disabled-active' : '') : 'disabled-active'}`}>
             <SkipNav>
               <a href="#content">컨텐츠 바로가기</a>
             </SkipNav>
             <HeaderContent id="headerContainer">
               <HeaderLogo>
                 <a href={RoutesString.Main}>
                   <HideText className="hide-txt">e편한세상</HideText>
                 </a>
               </HeaderLogo>
               <GnbWrapper isMain={isMain} sticky={isTouched} />
               <UtilWrapper
                 isMain={isMain}
                 isLogin={Boolean(userData)}
                 isSearchArea={isSearchArea}
                 setIsSearchArea={setIsSearchArea}
                 setMyMenuOpen={setIsMyMenuOpen}
                 menuOpen={isMyMenuOpen}
               />
             </HeaderContent>
           </HeaderWrapper>
         </HeaderRootContainer>
       </>
   );
 });
 
 export default Header;
 